/**
 * resolve.ts — the introspection spine: turn an opaque id into a human name.
 *
 * Every entity in Lineage is addressed by a URN (`urn:egae:asset:0f3a`). A URN is
 * correct for the machine and useless to a person. This module maps any id back to
 * a `ResolvedRef` — a short human label, a disambiguating sub-label, and the route
 * where the thing can be seen — by introspecting the live workspace.
 *
 * It is PURE and side-effect-free, so the same resolver serves three readers:
 *   • command-palette forms (raw-id fields render as searchable name-pickers),
 *   • query contracts (rows carry labels, never bare ids),
 *   • the Assistant (it can say "the alignment interview" instead of a URN).
 *
 * IDs should never be the thing that finally displays. This is where that promise
 * is kept.
 */
import type { Workspace } from "../store/events.ts";
import { isUrn, kindOf, type Urn } from "./urn.ts";
import { PRIMITIVE_BY_URN } from "./primitives.ts";

/** A resolved, human-facing handle on an entity. `label` is never a raw id. */
export interface ResolvedRef {
  /** The original id, kept so callers can still address the machine. */
  readonly id: string;
  /** Entity kind slug: session | evidence | interpretation | asset | … | "unknown". */
  readonly kind: string;
  /** The human name to display. Falls back to a short id only when nothing is known. */
  readonly label: string;
  /** A disambiguator — status, version, count — shown faintly beside the label. */
  readonly sublabel?: string;
  /** Hash route (no leading #) where this entity can be viewed, when one exists. */
  readonly route?: string;
  /** True when the id resolved to a real entity in the workspace (vs. a dangling ref). */
  readonly resolved: boolean;
}

/** Clip a one-line label so a name never becomes a paragraph. */
function clip(s: string, n = 64): string {
  const t = s.trim().replace(/\s+/g, " ");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

/** The first sentence/line of a longer body, for entities whose "name" is prose. */
function gist(s: string): string {
  const first = s.trim().split(/(?<=[.!?])\s|\n/)[0] ?? s;
  return clip(first);
}

/** A short, friendly tail of a URN for the last-resort label (`…0f3a`). */
function shortId(id: string): string {
  const tail = id.split(":").pop() ?? id;
  return tail.length > 10 ? `…${tail.slice(-6)}` : tail;
}

/** Map an entity kind to the page that shows it (and, for sessions, to its own page). */
function routeFor(kind: string, id: string): string | undefined {
  switch (kind) {
    case "session":
      return `/session/${id}`;
    case "evidence":
      return "/evidence";
    case "interpretation":
      return "/interpret";
    case "asset":
      return "/author";
    case "evaluation":
      return "/evaluate";
    case "pack":
      return "/package";
    case "projection":
      return "/project";
    case "trace":
      return "/trace";
    default:
      return undefined;
  }
}

/**
 * Resolve a single id against the workspace. Never throws and never returns a bare
 * URN as the label: an unknown id degrades to its short tail with `resolved:false`,
 * so a dangling reference is visible as such instead of masquerading as a name.
 */
export function resolveRef(ws: Workspace, id: string): ResolvedRef {
  const kind = isUrn(id) ? kindOf(id as Urn) ?? "unknown" : "unknown";
  const route = routeFor(kind, id);
  const base = { id, kind, ...(route ? { route } : {}) };

  switch (kind) {
    case "session": {
      const s = ws.session[id];
      if (!s) break;
      return { ...base, resolved: true, label: clip(s.title || gist(s.scope)), sublabel: `${s.status} · ${s.evidenceRefs.length} evidence` };
    }
    case "evidence": {
      const e = ws.evidence[id];
      if (!e) break;
      const name = e.normalizedSummary ? gist(e.normalizedSummary) : e.sourceType;
      // elicitationPrimitive is itself a urn — resolve it to the instrument's name.
      const method = PRIMITIVE_BY_URN[e.elicitationPrimitive]?.name ?? e.sourceType;
      return { ...base, resolved: true, label: clip(name), sublabel: `${method} · ${e.interpretationStatus}` };
    }
    case "interpretation": {
      const it = ws.interpretation[id];
      if (!it) break;
      return { ...base, resolved: true, label: gist(it.claim), sublabel: `${it.reviewStatus} · confidence ${it.confidence}` };
    }
    case "asset": {
      const a = ws.asset[id];
      if (!a) break;
      return { ...base, resolved: true, label: clip(a.title), sublabel: `${a.status} · v${a.version}${a.projectionEligible ? " · projection-eligible" : ""}` };
    }
    case "evaluation": {
      const ev = ws.evaluation[id];
      if (!ev) break;
      // An evaluation's name is best read through its target — recurse one hop.
      const target = resolveRef(ws, ev.targetRef);
      return { ...base, resolved: true, label: `Evaluation · ${target.label}`, sublabel: `${ev.verdict} · ${ev.findings.length} findings` };
    }
    case "pack": {
      const pk = ws.pack[id];
      if (!pk) break;
      return { ...base, resolved: true, label: clip(pk.title), sublabel: `${pk.status} · v${pk.version} · ${pk.assetVersionRefs.length} assets` };
    }
    case "projection": {
      const pr = ws.projection[id];
      if (!pr) break;
      return { ...base, resolved: true, label: clip(pr.projectionScope), sublabel: `${pr.status} · ${pr.contextBlocks.length} context blocks` };
    }
    case "trace": {
      const tr = ws.trace[id];
      if (!tr) break;
      return { ...base, resolved: true, label: gist(tr.output), sublabel: `${tr.firedContextBlocks.length} blocks fired` };
    }
  }

  // Fell through: unknown kind or dangling reference.
  return { ...base, resolved: false, label: kind === "unknown" ? clip(id, 40) : `${kind} ${shortId(id)}` };
}

/** Just the label — the common case for inline substitution. */
export function resolveLabel(ws: Workspace, id: string): string {
  return resolveRef(ws, id).label;
}

/** Resolver-aware collection slug for a kind (kinds and collections share names). */
const COLLECTION: Record<string, keyof Workspace> = {
  session: "session",
  evidence: "evidence",
  interpretation: "interpretation",
  asset: "asset",
  evaluation: "evaluation",
  pack: "pack",
  projection: "projection",
  trace: "trace",
};

/**
 * Every entity of a kind as a resolved ref — the option list a name-picker renders
 * instead of a free-text id field. Newest first (ids carry no order, so we sort by
 * the entity's `createdAt` when present).
 */
export function listRefs(ws: Workspace, kind: string): ResolvedRef[] {
  const slug = COLLECTION[kind];
  if (!slug) return [];
  const entities = ws[slug] as Record<string, { createdAt?: string }>;
  return Object.keys(entities)
    .sort((a, b) => (entities[b]?.createdAt ?? "").localeCompare(entities[a]?.createdAt ?? ""))
    .map((id) => resolveRef(ws, id));
}

/**
 * Infer which entity kind an input FIELD references, from its name — so a form can
 * render a name-picker instead of a raw-id text box. Field names are regular
 * (`sessionId`, `assetIds`, `projectionRef`), with three aliases for the cases where
 * the field name and the collection slug diverge. Returns undefined for fields that
 * are not entity references (e.g. `agentDefinitionId`, `projectionScope`, `id`).
 */
const FIELD_KIND_ALIAS: Record<string, string> = {
  doctrinepack: "pack",
  runtimeprojection: "projection",
  target: "asset",
};
export function refKindForField(field: string): string | undefined {
  const base = field.replace(/(Ids|Id|Refs|Ref)$/, "").toLowerCase();
  const kind = FIELD_KIND_ALIAS[base] ?? base;
  return kind in COLLECTION ? kind : undefined;
}

/** Fuzzy-match refs of a kind against a query over label + sub-label (for pickers). */
export function searchRefs(ws: Workspace, kind: string, query: string): ResolvedRef[] {
  const q = query.trim().toLowerCase();
  const all = listRefs(ws, kind);
  if (!q) return all;
  return all.filter((r) => `${r.label} ${r.sublabel ?? ""}`.toLowerCase().includes(q));
}
